// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/connectors/ConnectorAuthorizationHistory.jsx

import React from "react";

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleize(value) {
  return String(value || "connector_event")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function providerLabel(value) {
  const labels = {
    aws: "AWS",
    cloudflare: "Cloudflare",
    github: "GitHub",
    google_workspace: "Google Workspace",
    okta: "Okta",
  };
  return labels[value] || titleize(value);
}

function normalizedEventType(event) {
  return String(event?.event_type || "").trim().toLowerCase();
}

function normalizedReason(event) {
  return String(event?.reason || "").trim();
}

function containsFailureLanguage(value) {
  return /\b(fail(?:ed|ure)?|error|invalid|denied|missing|unable|could not|not attached|not configured)\b/i.test(String(value || ""));
}

function eventPresentation(event) {
  const type = normalizedEventType(event);

  if (type.includes("health") && type.includes("pass")) return ["Health check passed", "Live provider access was confirmed.", "success"];
  if (type.includes("health") && (type.includes("fail") || type.includes("error"))) return ["Health check needs attention", "OASYS could not fully confirm provider access.", "failure"];
  if (type.includes("reverif") && type.includes("pass")) return ["Connection reverified", "The stored read-only authorization was verified again.", "success"];
  if (type.includes("validat") && type.includes("pass")) return ["Live verification passed", "Provider identity and read-only access were confirmed.", "success"];
  if (type.includes("validat") && (type.includes("fail") || type.includes("error"))) return ["Live verification needs attention", "The provider connection could not be verified.", "failure"];
  if (type.includes("disconnect")) return ["Connector disconnected", "OASYS stopped contacting this provider.", "neutral"];
  if (type.includes("reconnect")) return ["Reconnect started", "Fresh provider verification was requested.", "neutral"];
  if (type.includes("pause")) return ["Connector paused", "Governance activity was paused without deleting history.", "neutral"];
  if (type.includes("resume")) return ["Connector resumed", "Governance activity was resumed.", "success"];
  if (type.includes("owner")) return ["Ownership updated", "Connector accountability was updated.", "neutral"];
  if (type.includes("governance")) return ["Governance updated", "Business purpose or governance context changed.", "neutral"];
  if (type.includes("complete") || type.includes("connect")) return ["Connector connected", "The governed enterprise connection became active.", "success"];
  if (type.includes("start")) return ["Setup started", "Connector authorization setup was opened.", "neutral"];
  return [titleize(event?.event_type), "Connector lifecycle activity was recorded.", "neutral"];
}

function customerReason(event, tone) {
  const reason = normalizedReason(event);
  if (!reason) return null;
  const failureReason = containsFailureLanguage(reason);
  if (tone === "success" && failureReason) return null;
  if (tone === "failure" && !failureReason) return null;
  return reason;
}

export default function ConnectorAuthorizationHistory({ events = [], connectorId = null }) {
  const filtered = (connectorId
    ? events.filter((event) => String(event.connector_id) === String(connectorId))
    : events
  ).slice(0, 8);

  return (
    <section className="enterprise-admin-form-card enterprise-admin-connector-history">
      <div className="enterprise-admin-card-heading enterprise-admin-connector-card-heading">
        <p className="enterprise-admin-eyebrow">Recent activity</p>
        <h3>{connectorId ? "Selected provider activity" : "Recent connector activity"}</h3>
        <p>Connection, verification, health, and governance events for the selected provider.</p>
      </div>

      {!filtered.length ? (
        <div className="enterprise-admin-empty compact">
          <strong>No recent connector activity</strong>
          <p>Connection and governance events will appear here.</p>
        </div>
      ) : (
        <div className="enterprise-admin-audit-list enterprise-admin-connector-audit-list">
          {filtered.map((event) => {
            const [label, detail, tone] = eventPresentation(event);
            const visibleReason = customerReason(event, tone);

            return (
              <article
                key={event.id}
                className={`enterprise-admin-audit-event enterprise-admin-connector-audit-event is-${tone}`}
              >
                <div className="enterprise-admin-connector-audit-content">
                  <span className="enterprise-admin-connector-audit-marker" aria-hidden="true" />
                  <div>
                    <strong>{label}</strong>
                    <span>{providerLabel(event.provider_key)}</span>
                    <span>{detail}</span>
                    {visibleReason ? <span>{visibleReason}</span> : null}
                    {event.previous_status || event.new_status ? (
                      <details className="enterprise-admin-connector-technical-detail">
                        <summary>Technical detail</summary>
                        <span>{titleize(event.previous_status || "none")} → {titleize(event.new_status || "none")}</span>
                      </details>
                    ) : null}
                  </div>
                </div>
                <time>{dateLabel(event.created_at)}</time>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
